import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, CreditCard, RefreshCw } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusPill = (status) => {
  const map = {
    pending_link_or_manual: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
    awaiting_delivery: 'border-sky-400/20 bg-sky-500/10 text-sky-200',
    ready_to_charge: 'border-primary/20 bg-primary/10 text-primary',
    paid: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
    failed: 'border-rose-400/20 bg-rose-500/10 text-rose-200',
    active: 'border-primary/20 bg-primary/10 text-primary',
    scheduled_after_delivery: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
  };
  return map[(status || '').toLowerCase()] || 'border-border-subtle bg-card/40 text-text-muted';
};

const MetricCard = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        <p className="mt-2 text-xs text-text-muted">{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
    </div>
  </div>
);

export const BillingControlHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [contractForm, setContractForm] = useState({
    company_name: 'Clínica estética demo',
    client_email: 'clinica.demo@example.com',
    package_name: 'Diagnóstico + Activación 369',
    project_total_amount: '869',
    delivery_due_at: '',
    maintenance_monthly_amount: '49',
    maintenance_cycles_total: '3',
    notes: '50% inicio, 50% entrega, mantenimiento a 30 días de la entrega.',
  });

  const loadHub = useCallback(async (force = false) => {
    try {
      setLoading(force || !hub);
      const res = await authFetch(`${API}/admin/billing/contracts`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el control de cobros');
      setHub(data);
      if (force) toast.success('Control de cobros actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el control de cobros');
    } finally {
      setLoading(false);
    }
  }, [authFetch, hub]);

  useEffect(() => {
    loadHub(false);
  }, [loadHub]);

  const runAction = async (contractId, action) => {
    try {
      setSavingId(`${contractId}-${action}`);
      const res = await authFetch(`${API}/admin/billing/contracts/${contractId}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo completar la acción');
      toast.success(action === 'mark-delivered' ? 'Proyecto marcado como entregado' : 'Cobro lanzado correctamente');
      await loadHub(true);
    } catch (error) {
      toast.error(error.message || 'No se pudo completar la acción');
    } finally {
      setSavingId(null);
    }
  };

  const launchDepositCheckout = async (contractId) => {
    try {
      setSavingId(`${contractId}-deposit-checkout`);
      const res = await authFetch(`${API}/admin/billing/contracts/${contractId}/deposit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo abrir el checkout del 50% inicial');
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'No se pudo abrir el checkout del 50% inicial');
    } finally {
      setSavingId(null);
    }
  };

  const createContract = async (e) => {
    e.preventDefault();
    try {
      setSavingId('create-contract');
      const res = await authFetch(`${API}/admin/billing/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contractForm,
          project_total_amount: Number(contractForm.project_total_amount || 0),
          maintenance_monthly_amount: Number(contractForm.maintenance_monthly_amount || 0),
          maintenance_cycles_total: Number(contractForm.maintenance_cycles_total || 0),
          currency: 'usd',
          package_id: 'auditoria_ia',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear el contrato de cobro');
      toast.success('Contrato de cobro creado correctamente');
      await loadHub(true);
    } catch (error) {
      toast.error(error.message || 'No se pudo crear el contrato de cobro');
    } finally {
      setSavingId(null);
    }
  };

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="billing-control-loading">Cargando control de cobros…</div>;

  return (
    <div className="space-y-6" data-testid="billing-control-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="billing-control-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-5">Cobros y facturación</p>
            <h3 className="text-3xl font-semibold text-text-heading">Control total del 50% inicial, 50% final y mantenimientos automáticos</h3>
            <p className="mt-4 max-w-3xl text-base text-text-body">Este módulo pertenece a <strong>Neuralia Solutions</strong>: aquí controlas clientes finales, entregas, cargos pendientes y mantenimientos. No mezcla lógica de comisiones partner, aunque conviva dentro del mismo ecosistema.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard icon={CreditCard} label="Contratos" value={hub.metrics?.contracts || 0} helper="controlados en este panel" testId="billing-control-metric-contracts" />
            <MetricCard icon={AlertTriangle} label="50% listos" value={hub.metrics?.balance_ready_to_charge || 0} helper="pendientes de ejecutar con un clic" testId="billing-control-metric-balances" />
            <MetricCard icon={CalendarClock} label="Mantenimientos" value={hub.metrics?.maintenance_active || 0} helper="series activas o programadas" testId="billing-control-metric-maintenance" />
            <MetricCard icon={RefreshCw} label="Fallos" value={hub.metrics?.failed_autocharges || 0} helper="requieren revisión manual" testId="billing-control-metric-failures" />
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="billing-control-alert-destination-card">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Notificaciones ejecutivas</p>
        <h4 className="mt-2 text-xl font-semibold text-text-heading">Destino actual preparado</h4>
        <p className="mt-3 text-sm text-text-body">WhatsApp ejecutivo configurado en settings: <span className="text-text-heading">{hub.ceo_alert_settings?.whatsapp_number || '+34653675515'}</span></p>
        <p className="mt-2 text-sm text-text-body">Estado real: preparado en cola interna, pendiente de credenciales reales de WhatsApp Cloud API para envío automático efectivo.</p>
      </div>

      <form onSubmit={createContract} className="glass-card p-6 space-y-3" data-testid="billing-control-create-contract-form">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Alta rápida</p>
        <h4 className="text-xl font-semibold text-text-heading">Crear contrato de cobro visible en admin</h4>
        <div className="grid gap-3 md:grid-cols-2">
          <input className="input-kinesthetic" placeholder="Empresa cliente" value={contractForm.company_name} onChange={(e) => setContractForm((prev) => ({ ...prev, company_name: e.target.value }))} data-testid="billing-control-company-name-input" />
          <input className="input-kinesthetic" placeholder="Email cliente" value={contractForm.client_email} onChange={(e) => setContractForm((prev) => ({ ...prev, client_email: e.target.value }))} data-testid="billing-control-client-email-input" />
          <input className="input-kinesthetic" placeholder="Paquete" value={contractForm.package_name} onChange={(e) => setContractForm((prev) => ({ ...prev, package_name: e.target.value }))} data-testid="billing-control-package-name-input" />
          <input className="input-kinesthetic" type="number" min="1" placeholder="Total proyecto USD" value={contractForm.project_total_amount} onChange={(e) => setContractForm((prev) => ({ ...prev, project_total_amount: e.target.value }))} data-testid="billing-control-project-total-input" />
          <input className="input-kinesthetic" type="datetime-local" value={contractForm.delivery_due_at} onChange={(e) => setContractForm((prev) => ({ ...prev, delivery_due_at: e.target.value }))} data-testid="billing-control-delivery-due-input" />
          <input className="input-kinesthetic" type="number" min="0" placeholder="Mantenimiento mensual" value={contractForm.maintenance_monthly_amount} onChange={(e) => setContractForm((prev) => ({ ...prev, maintenance_monthly_amount: e.target.value }))} data-testid="billing-control-maintenance-amount-input" />
          <input className="input-kinesthetic" type="number" min="0" max="12" placeholder="Ciclos mantenimiento" value={contractForm.maintenance_cycles_total} onChange={(e) => setContractForm((prev) => ({ ...prev, maintenance_cycles_total: e.target.value }))} data-testid="billing-control-maintenance-cycles-input" />
        </div>
        <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas" value={contractForm.notes} onChange={(e) => setContractForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="billing-control-notes-input" />
        <button type="submit" className="btn-primary" disabled={savingId === 'create-contract'} data-testid="billing-control-create-contract-submit-button">{savingId === 'create-contract' ? 'Creando...' : 'Crear contrato de cobro'}</button>
      </form>

      <div className="space-y-4" data-testid="billing-control-contracts-list">
        {(hub.contract_cards || []).map((contract) => (
          <div key={contract.id} className="glass-card p-6" data-testid={`billing-control-contract-${contract.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap gap-2">
                  <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(contract.deposit_status)}`}>depósito {contract.deposit_status}</span>
                  <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(contract.balance_status)}`}>saldo {contract.balance_status}</span>
                  <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${statusPill(contract.maintenance_status)}`}>mantenimiento {contract.maintenance_status}</span>
                </div>
                <h4 className="mt-3 text-2xl font-semibold text-text-heading">{contract.company_name}</h4>
                <p className="mt-2 text-sm text-text-body">{contract.package_name} · {contract.client_email}</p>
              </div>
              <button className="btn-secondary" onClick={() => loadHub(true)} data-testid={`billing-control-refresh-${contract.id}`}>
                <RefreshCw className="h-4 w-4" /> Refrescar
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-4">
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`billing-control-total-${contract.id}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Proyecto</p>
                <p className="mt-2 text-xl font-semibold text-text-heading">${Number(contract.project_total_amount || 0).toLocaleString('en-US')}</p>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`billing-control-deposit-${contract.id}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Primer 50%</p>
                <p className="mt-2 text-xl font-semibold text-text-heading">${Number(contract.deposit_amount || 0).toLocaleString('en-US')}</p>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`billing-control-balance-${contract.id}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Segundo 50%</p>
                <p className="mt-2 text-xl font-semibold text-text-heading">${Number(contract.balance_amount || 0).toLocaleString('en-US')}</p>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`billing-control-maintenance-${contract.id}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Mantenimiento</p>
                <p className="mt-2 text-xl font-semibold text-text-heading">${Number(contract.maintenance_monthly_amount || 0).toLocaleString('en-US')} × {contract.maintenance_cycles_total || 0}</p>
              </div>
            </div>

            <div className="mt-5 rounded-[20px] border border-border-subtle bg-card/40 p-4 text-sm text-text-body" data-testid={`billing-control-dates-${contract.id}`}>
              <p>Entrega prevista: <span className="text-text-heading">{contract.delivery_due_at ? new Date(contract.delivery_due_at).toLocaleString('es-ES') : 'pendiente'}</span></p>
              <p className="mt-2">Próximo cobro mantenimiento: <span className="text-text-heading">{contract.maintenance_next_charge_at ? new Date(contract.maintenance_next_charge_at).toLocaleString('es-ES') : 'pendiente'}</span></p>
              <p className="mt-2">Ciclos cobrados: <span className="text-text-heading">{contract.maintenance_cycles_charged || 0}/{contract.maintenance_cycles_total || 0}</span></p>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" className="btn-secondary" onClick={() => launchDepositCheckout(contract.id)} disabled={savingId === `${contract.id}-deposit-checkout`} data-testid={`billing-control-launch-deposit-${contract.id}`}>
                Cobrar primer 50%
              </button>
              <button type="button" className="btn-primary" onClick={() => runAction(contract.id, 'mark-delivered')} disabled={savingId === `${contract.id}-mark-delivered`} data-testid={`billing-control-mark-delivered-${contract.id}`}>
                Marcar entregado
              </button>
              <button type="button" className="btn-secondary" onClick={() => runAction(contract.id, 'charge-balance')} disabled={savingId === `${contract.id}-charge-balance`} data-testid={`billing-control-charge-balance-${contract.id}`}>
                Cobrar segundo 50%
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillingControlHub;